import { omit } from "lodash";
import { PaginatedResponse } from "./common";
import { emptySplitApi } from "./empty-split-api";
import {
  ReferralSource,
  ReferralSourceChildrenQuery,
  ReferralSourceFilter,
  ReferralSourceInput,
  ReferralSourceResponse,
  ReferralSourceRule,
  ReferralSourceRuleCreateInput,
  ReferralSourceRuleDeleteInput,
  ReferralSourceRuleUpdateInput,
  SubReferralSource,
} from "./types/referral-source.type";

export const referralSourcesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getReferralSources: builder.query<
      ReferralSourceResponse,
      ReferralSourceFilter | void
    >({
      query: (filter) =>
        `/referral-sources/?parent__isnull=true&query={-children}${
          filter ? `&is_active=${filter.is_active}` : ""
        }`,
      providesTags: ["ReferralSources"],
    }),
    getSubReferralSources: builder.query<
      ReferralSourceResponse,
      ReferralSourceFilter | void
    >({
      query: (filter) =>
        `/referral-sources/?parent__isnull=false&query={-children}${
          filter ? `&is_active=${filter.is_active}` : ""
        }`,
      providesTags: ["SubReferralSources"],
    }),
    getReferralSource: builder.query<ReferralSource, number>({
      query: (referralSourceId) => `/referral-sources/${referralSourceId}/`,
      providesTags: ["ReferralSources"],
    }),
    addReferralSource: builder.mutation<ReferralSource, ReferralSourceInput>({
      query: (referralSource) => ({
        url: "/referral-sources/",
        method: "POST",
        body: referralSource,
      }),
      invalidatesTags: ["ReferralSources", "ReferralSourceChildren"],
    }),
    editReferralSource: builder.mutation<ReferralSource, ReferralSourceInput>({
      query: (referralSource) => ({
        url: `/referral-sources/${referralSource.id}/`,
        method: "PATCH",
        body: omit(referralSource, "id"),
      }),
      invalidatesTags: ["ReferralSources", "ReferralSourceChildren"],
    }),
    getReferralSourceChildren: builder.query<
      PaginatedResponse<SubReferralSource>,
      ReferralSourceChildrenQuery
    >({
      query: ({ referralSourceId, limit = 25, offset = 0, search }) => {
        let url = `/referral-sources/${referralSourceId}/children/?limit=${limit}&offset=${offset}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        return url;
      },
      providesTags: ["ReferralSourceChildren"],
    }),
    getReferralSourceRules: builder.query<
      PaginatedResponse<ReferralSourceRule>,
      number
    >({
      query: (referralSourceId) =>
        `/referral-sources/${referralSourceId}/rules/`,
      transformResponse: (response: any): PaginatedResponse<ReferralSourceRule> => {
        if (Array.isArray(response)) {
          return { count: response.length, next: null, previous: null, results: response };
        }
        return response;
      },
      providesTags: (result, error, referralSourceId) => [
        { type: "ReferralSourceRules" as const, id: referralSourceId },
      ],
    }),
    createReferralSourceRule: builder.mutation<
      ReferralSourceRule,
      ReferralSourceRuleCreateInput
    >({
      query: ({ referralSourceId, ...body }) => ({
        url: `/referral-sources/${referralSourceId}/rules/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { referralSourceId }) => [
        { type: "ReferralSourceRules" as const, id: referralSourceId },
      ],
    }),
    updateReferralSourceRule: builder.mutation<
      ReferralSourceRule,
      ReferralSourceRuleUpdateInput
    >({
      query: ({ referralSourceId, id, ...body }) => ({
        url: `/referral-sources/${referralSourceId}/rules/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { referralSourceId }) => [
        { type: "ReferralSourceRules" as const, id: referralSourceId },
      ],
    }),
    deleteReferralSourceRule: builder.mutation<void, ReferralSourceRuleDeleteInput>({
      query: ({ referralSourceId, id }) => ({
        url: `/referral-sources/${referralSourceId}/rules/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { referralSourceId }) => [
        { type: "ReferralSourceRules" as const, id: referralSourceId },
      ],
    }),
  }),
});

export const {
  useGetReferralSourcesQuery,
  useGetSubReferralSourcesQuery,
  useGetReferralSourceQuery,
  useGetReferralSourceChildrenQuery,
  useAddReferralSourceMutation,
  useEditReferralSourceMutation,
  useGetReferralSourceRulesQuery,
  useCreateReferralSourceRuleMutation,
  useUpdateReferralSourceRuleMutation,
  useDeleteReferralSourceRuleMutation,
} = referralSourcesApi;
